import { useGLTF, OrbitControls, useAnimations, Stage } from '@react-three/drei'
import { useEffect, useMemo, useRef } from 'react'
import { useThree } from '@react-three/fiber'
import { useControls, button } from 'leva'
import * as THREE from 'three'

export default function Geos() {
  // const model = useGLTF('./ALIREZAGLTF7animaTEXTU3.ACTnn.gltf')
  const model = useGLTF(`${import.meta.env.BASE_URL}207/glb/207car.glb`)
  const { actions, mixer } = useAnimations(model.animations, model.scene)
  const { camera } = useThree()
  const controlsRef = useRef()

  // Parts permanently removed from the scene (not shown in Hide Parts either)
  const hiddenByDefault = useMemo(
    () => new Set(['Plane.086', 'Plane.085', 'Text', 'Text.001']),
    []
  )

  // Hide selected parts permanently, collect meshes & materials for controls
  const { materials, meshes } = useMemo(() => {
    const isHidden = (name) =>
      hiddenByDefault.has(name) || /^Plane\.?090(_\d+)?$/.test(name)

    const BODY_PAINT = 'Material.013'
    const DEFAULT_BODY_COLOR = '#000000'

    const foundMats = new Map()
    const foundMeshes = []
    model.scene.traverse((child) => {
      if (isHidden(child.name)) {
        child.visible = false
        return
      }
      if (!child.isMesh || !child.material) return
      foundMeshes.push(child)
      const mats = Array.isArray(child.material) ? child.material : [child.material]
      mats.forEach((mat) => {
        if (!mat || !mat.color) return
        if (mat.name === BODY_PAINT) mat.color.set(DEFAULT_BODY_COLOR)
        if (!foundMats.has(mat.uuid)) foundMats.set(mat.uuid, mat)
      })
    })
    return {
      materials: Array.from(foundMats.values()),
      meshes: foundMeshes,
    }
  }, [model.scene, hiddenByDefault])

  const colorControls = useControls('Part Colors', () => {
    const usedLabels = {}
    const schema = {}
    materials.forEach((mat) => {
      let label = mat.name || 'Unnamed'
      usedLabels[label] = (usedLabels[label] || 0) + 1
      if (usedLabels[label] > 1) label = `${label} (${usedLabels[label]})`
      schema[mat.uuid] = {
        value: `#${mat.color.getHexString()}`,
        label,
        onChange: (hex) => mat.color.set(hex),
      }
    })
    return schema
  }, { collapsed: true }, [materials])

  useControls('Hide Parts', () => {
    const usedLabels = {}
    const schema = {}
    meshes.forEach((mesh) => {
      let label = mesh.name || 'Unnamed'
      usedLabels[label] = (usedLabels[label] || 0) + 1
      if (usedLabels[label] > 1) label = `${label} (${usedLabels[label]})`
      schema[mesh.uuid] = {
        value: false,
        label,
        onChange: (hidden) => {
          mesh.visible = !hidden
        },
      }
    })
    return schema
  }, { collapsed: true }, [meshes])

  const { fov, autoRotate, rotateSpeed } = useControls('Camera', {
    fov: { value: 75, min: 20, max: 120, step: 1, label: 'Field of View' },
    autoRotate: { value: false, label: 'Auto Rotate' },
    rotateSpeed: { value: 1, min: 0.1, max: 5, step: 0.1, label: 'Rotate Speed' },
    'Reset Camera': button(() => controlsRef.current?.reset()),
  })

  const { background, wireframe } = useControls('Scene', {
    background: { value: '#ffa500', label: 'Background Color' },
    wireframe: { value: false, label: 'Wireframe' },
  })

  const playSequenceRef = useRef(() => {})

  const { animationSpeed } = useControls('Animation', {
    animationSpeed: { value: 1, min: 0, max: 3, step: 0.1, label: 'Playback Speed' },
    'Replay': button(() => playSequenceRef.current()),
  })

  useEffect(() => {
    camera.fov = fov
    camera.updateProjectionMatrix()
  }, [fov, camera])

  useEffect(() => {
    materials.forEach((mat) => { mat.wireframe = wireframe })
  }, [wireframe, materials])

  useEffect(() => {
    mixer.timeScale = animationSpeed
  }, [animationSpeed, mixer])

  useEffect(() => {
    if (!model.animations || model.animations.length === 0) return

    const group1 = [model.animations[0], model.animations[2], model.animations[3]]
    const group2 = [model.animations[1], model.animations[4]]

    const actionsGroup1 = group1.map((clip) => {
      const action = mixer.clipAction(clip)
      action.setLoop(THREE.LoopOnce)
      action.clampWhenFinished = true
      return action
    })

    const actionsGroup2 = group2.map((clip) => {
      const action = mixer.clipAction(clip)
      action.setLoop(THREE.LoopOnce)
      action.clampWhenFinished = true
      return action
    })

    let finishedCount = 0

    const onActionFinished = () => {
      finishedCount++
      if (finishedCount === actionsGroup1.length) {
        // Stop and reset all actions of group 1
        actionsGroup1.forEach((action) => {
          action.stop()
          action.reset()
        })

        // Optionally reset model pose to default (no animation)
        mixer.setTime(0)
        mixer.update(0)

        // Start group 2 animations
        actionsGroup2.forEach((action) => {
          action.reset()
          action.fadeIn(0.3)
          action.play()
        })

        mixer.removeEventListener('finished', onActionFinished)
      }
    }

    const playSequence = () => {
      finishedCount = 0
      mixer.removeEventListener('finished', onActionFinished)

      // Stop everything and go back to the resting pose before replaying
      actionsGroup1.forEach((action) => {
        action.stop()
        action.reset()
      })
      actionsGroup2.forEach((action) => {
        action.stop()
        action.reset()
      })
      mixer.setTime(0)
      mixer.update(0)

      mixer.addEventListener('finished', onActionFinished)

      // Start group 1 animations
      actionsGroup1.forEach((action) => {
        action.reset()
        action.fadeIn(0.3)
        action.play()
      })
    }

    playSequenceRef.current = playSequence
    playSequence()

    return () => {
      mixer.removeEventListener('finished', onActionFinished)
    }
  }, [model.animations, mixer])

  return (
    <>
      <color attach="background" args={[background]} />
      <OrbitControls ref={controlsRef} makeDefault autoRotate={autoRotate} autoRotateSpeed={rotateSpeed} />
      <Stage environment="city">
        <primitive object={model.scene} />
      </Stage>
    </>
  )
}
